import { CommonSwitch } from '@/components/common/switch';
import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { Grid } from '@mui/material';
import { useWindowSize } from '@react-hook/window-size';
import { t } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DialogContext } from '../..';
import { getSettingVolumeState } from '../../selector';
import { volumeSliceActions } from '../../slice/volume';
import { VolumeSlider, VolumeType } from './VolumeSlider';
import './index.scss';
import { VideoPlayerContext } from '@/contexts/VideoContext';

/**
 * defualt value reference
 * Mute: false
 * Game Volume: 0%
 * Studio Volume: 50%
 **/

export const SoundSetting = (/*props: SettingDialogProps*/) => {
    // const { setIsOpen } = props;
    const { Mute, Game, Studio, CachedGame, CachedStudio } = useSelector(
        getSettingVolumeState
    );

    //game audio
    const { setMasterVolumn: setWebAudioVolume, setTestGameVolumeAudio } =
        useContext(AudioPlayContext);
    //studio audio
    const { hostId, changeVolume: setVideoPlayerVolume } =
        useContext(VideoPlayerContext);

    const [switchMute, setSwitchMute] = useState(Mute);
    const [studioVolume, setStudioVolume] = useState(Studio);
    const [gameVolume, setGameVolume] = useState(Game);

    const [cacheGameVolume, setCacheGameVolume] = useState<number>(CachedGame);
    const [cacheStudioVolume, setCacheStudioVolume] =
        useState<number>(CachedStudio);

    const { isLandscape, isApple } = useContext(ScreenContext);
    const [width, height] = useWindowSize({ wait: 100 });
    const [switchZoom, setSwitchZoom] = useState<number>(1.5);
    const { setExtraCloseHandler } = useContext(DialogContext);
    const dispatch = useDispatch();

    const mute = () => {
        console.log('soundsetting:function:mute');
        //cache the game volume and studio volume before mute
        setCacheGameVolume(gameVolume);
        setCacheStudioVolume(studioVolume);

        updateGameVolume(0);
        updateStudioVolume(0);

        //direct change the webaudio player volume
        setTestGameVolumeAudio(0);
    };

    const unmute = () => {
        console.log('soundsetting:function:unmute');
        //callback the game volume and studio volume when unmute
        updateGameVolume(cacheGameVolume);
        updateStudioVolume(cacheStudioVolume);

        //direct change the webaudio player volume
        setTestGameVolumeAudio(cacheGameVolume);
    };

    const updateGameVolume = (value: number) => {
        if (gameVolume === 0 && value === 0) return;
        // console.warn('soundsetting:function:update game volume:', value);
        //local value
        setGameVolume(value);
        //game - web audio
        setWebAudioVolume(value / 100);
    };

    const updateStudioVolume = (value: number) => {
        if (studioVolume === 0 && value === 0) return;
        // console.warn('soundsetting:function:update studio volume:', value);
        //local value
        setStudioVolume(value);
        //studio - video player
        if (hostId && setVideoPlayerVolume) setVideoPlayerVolume(value / 100);
    };

    useEffect(() => {
        setExtraCloseHandler(() => () => {
            console.log(
                'soundsetting:function:closesetting:dispatch to store:',
                '\nMute:',
                switchMute,
                '\nGame:',
                gameVolume,
                '\nStudio:',
                studioVolume,
                '\nCached Game:',
                cacheGameVolume,
                '\nCached Studio:',
                cacheStudioVolume
            );
            dispatch(
                volumeSliceActions.updateAll({
                    Studio: studioVolume,
                    Game: gameVolume,
                    Mute: switchMute,
                    // Music: musicVolume,
                    CachedStudio: cacheStudioVolume,
                    CachedGame: cacheGameVolume,
                })
            );
        });
    }, [studioVolume, gameVolume, switchMute]);

    // useEffect(() => {
    //     if (switchMute) {
    //         mute();
    //     } else {
    //         unmute();
    //     }
    // }, [switchMute]);

    useEffect(() => {
        setSwitchMute(studioVolume === 0 && gameVolume === 0);
    }, [studioVolume, gameVolume]);
    useEffect(() => {
        const zoom = Math.max((height / 16) * 0.04, 1.5);
        setSwitchZoom(isLandscape ? zoom : 1.5);
    }, [width, height]);

    return (
        <Grid
            container
            direction={'column'}
            justifyContent={'space-between'}
            alignItems={'center'}
            className="sound-setting"
        >
            <Grid container className="grid-container">
                <Grid
                    container
                    direction={isApple ? 'row' : 'column'}
                    justifyContent={isApple ? 'space-between' : 'unset'}
                >
                    <Grid item className="sound-setting-item-title">
                        {t('setting.game_volume')}
                    </Grid>
                    {!isApple ? (
                        <VolumeSlider
                            type={VolumeType.Game}
                            volume={gameVolume}
                            handleVolumeChange={v => {
                                updateGameVolume(v);
                                updateStudioVolume(0);
                            }}
                        />
                    ) : (
                        <Grid item>
                            <CommonSwitch
                                sx={{ zoom: switchZoom }}
                                checked={gameVolume !== 0}
                                onChange={(_, checked) => {
                                    if (checked) {
                                        //local value
                                        setSwitchMute(false);
                                        updateGameVolume(100);
                                        updateStudioVolume(0);
                                    } else {
                                        updateGameVolume(0);
                                    }
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid
                    container
                    direction={isApple ? 'row' : 'column'}
                    justifyContent={isApple ? 'space-between' : 'unset'}
                    className="sound-setting-item"
                >
                    <Grid item className="sound-setting-item-title">
                        {t('setting.studio_sound')}
                    </Grid>
                    {!isApple ? (
                        <Grid item>
                            <VolumeSlider
                                type={VolumeType.Studio}
                                volume={studioVolume}
                                handleVolumeChange={v => {
                                    updateStudioVolume(v);
                                    updateGameVolume(0);
                                }}
                            />
                        </Grid>
                    ) : (
                        <Grid item>
                            <CommonSwitch
                                sx={{ zoom: switchZoom }}
                                checked={studioVolume !== 0}
                                onChange={(_, checked) => {
                                    if (checked) {
                                        setSwitchMute(false);
                                        updateStudioVolume(100);
                                        updateGameVolume(0);
                                    } else {
                                        updateStudioVolume(0);
                                    }
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid
                    container
                    direction={'row'}
                    width={'100%'}
                    justifyContent={'space-between'}
                >
                    <Grid item className="sound-setting-item-title">
                        {t('setting.mute')}
                    </Grid>
                    <Grid item>
                        <CommonSwitch
                            sx={{ zoom: switchZoom }}
                            checked={switchMute}
                            onChange={(_, checked) => {
                                if (checked) {
                                    mute();
                                } else {
                                    unmute();
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
